import React, { useEffect, useState } from 'react';
import {
	AlertCircle,
	ArrowLeft,
	ArrowRight,
	CheckCircle2,
	ExternalLink,
	FileText,
	Lightbulb,
	MinusCircle,
	XCircle,
} from 'lucide-react';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';

const FILTER_ORDER = ['all', 'incorrect', 'unanswered', 'correct'];

const FILTER_LABELS = {
	all: 'Todas',
	incorrect: 'Incorrectas',
	unanswered: 'Sin responder',
	correct: 'Correctas',
};

function normalizeImageUrl(rawUrl) {
	if (typeof rawUrl !== 'string') return null;
	const value = rawUrl.trim();
	if (!value) return null;

	const lowered = value.toLowerCase();
	if (lowered === 'null' || lowered === 'undefined') return null;

	return value;
}

function normalizeBase64Image(rawBase64) {
	if (typeof rawBase64 !== 'string') return null;

	const value = rawBase64.trim();
	if (!value) return null;

	const lowered = value.toLowerCase();
	if (lowered === 'null' || lowered === 'undefined') return null;

	const clean = value.replace(/\s+/g, '');
	if (clean.length < 32) return null;
	if (!/^[A-Za-z0-9+/=]+$/.test(clean)) return null;

	return clean;
}

function resolveQuestionImagePayload(questionPayload) {
	const question = questionPayload?.question || {};
	const clinicalCase = questionPayload?.clinical_case || {};

	const questionMedia = Array.isArray(questionPayload?.media)
		? questionPayload.media
		: Array.isArray(question?.media)
		? question.media
		: [];

	const mediaImage = questionMedia.find(
		(media) => media?.media_type === 'image' && media?.base64_data
	);

	const imageUrl =
		normalizeImageUrl(question?.image_url) ||
		normalizeImageUrl(clinicalCase?.image_url);

	const imageAlt =
		mediaImage?.alt_text ||
		question?.image_alt_text ||
		clinicalCase?.image_alt_text ||
		'Imagen de la pregunta';

	if (imageUrl) {
		return { src: imageUrl, alt: imageAlt };
	}

	const base64Value =
		normalizeBase64Image(mediaImage?.base64_data) ||
		normalizeBase64Image(question?.image_base64) ||
		normalizeBase64Image(clinicalCase?.image_base64);

	if (!base64Value) return null;

	const mimeType =
		mediaImage?.mime_type ||
		(base64Value.startsWith('PHN2Zy') ? 'image/svg+xml' : 'image/png');

	return {
		src: `data:${mimeType};base64,${base64Value}`,
		alt: imageAlt,
	};
}

function getStatusTone(status) {
	if (status === 'correct') {
		return {
			label: 'Correcta',
			icon: <CheckCircle2 size={14} />,
			fg: '#15803d',
			bg: '#f0fdf4',
			border: '#86efac',
		};
	}

	if (status === 'incorrect') {
		return {
			label: 'Incorrecta',
			icon: <XCircle size={14} />,
			fg: '#dc2626',
			bg: '#fef2f2',
			border: '#fca5a5',
		};
	}

	return {
		label: 'Sin responder',
		icon: <MinusCircle size={14} />,
		fg: '#64748b',
		bg: '#f8fafc',
		border: '#cbd5e1',
	};
}

function getDifficultyTone(value) {
	const normalized = String(value || '').trim().toLowerCase();

	if (['hard', 'difficult', 'dificil', 'difícil'].includes(normalized)) {
		return { label: 'Difícil', fg: '#dc2626', bg: '#fef2f2', border: '#fca5a5' };
	}

	if (['easy', 'facil', 'fácil'].includes(normalized)) {
		return { label: 'Fácil', fg: '#15803d', bg: '#f0fdf4', border: '#86efac' };
	}

	return { label: 'Media', fg: '#b45309', bg: '#fffbeb', border: '#fcd34d' };
}

function normalizeOptionState(option, questionResult) {
	const isCorrect = Boolean(option?.is_correct);
	const wasSelected = Boolean(option?.was_selected);

	if (isCorrect) {
		return {
			border: '#86efac',
			bg: '#f0fdf4',
			badge: 'Respuesta correcta',
			badgeFg: '#15803d',
			badgeBg: '#dcfce7',
			accent: '#15803d',
			ring: '#22c55e',
		};
	}

	if (wasSelected && !questionResult?.is_correct) {
		return {
			border: '#fca5a5',
			bg: '#fef2f2',
			badge: 'Tu respuesta',
			badgeFg: '#dc2626',
			badgeBg: '#fee2e2',
			accent: '#dc2626',
			ring: '#ef4444',
		};
	}

	return {
		border: '#e2e8f0',
		bg: '#ffffff',
		badge: null,
		badgeFg: '#64748b',
		badgeBg: '#f8fafc',
		accent: '#334155',
		ring: '#cbd5e1',
	};
}

function optionNumber(position) {
	const value = Number(position);
	if (!Number.isFinite(value) || value < 1) return '-';
	return String(value);
}

function capitalizeFirstLetter(value) {
	const text = String(value || '');
	if (!text) return text;
	return text.charAt(0).toUpperCase() + text.slice(1);
}

export default function PreguntasBody({
	questionPayload,
	filters,
	activeFilter = 'all',
	onFilterChange,
	totalQuestions,
	onPrevious,
	onNext,
	onViewDetail,
	onOpenSource,
	disablePrevious = false,
	disableNext = false,
}) {
	const [hasImageError, setHasImageError] = useState(false);
	const question = questionPayload?.question || {};
	const result = questionPayload?.result || {};
	const specialty = questionPayload?.specialty || {};
	const topic = questionPayload?.topic || {};
	const options = Array.isArray(questionPayload?.options) ? questionPayload.options : [];

	const mergedFilters = {
		all: Number(filters?.all ?? 0),
		incorrect: Number(filters?.incorrect ?? 0),
		unanswered: Number(filters?.unanswered ?? 0),
		correct: Number(filters?.correct ?? 0),
	};

	const statusTone = getStatusTone(result?.status);
	const difficultyTone = getDifficultyTone(question?.difficulty);
	const questionPosition = Number(question?.position ?? 0);
	const questionTotal = Number(totalQuestions ?? filters?.all ?? 0);
	const sourceLabel = question?.source || questionPayload?.clinical_case?.extra_data?.source || 'Fuente no disponible';
	const sourceUrl = question?.source_url || questionPayload?.clinical_case?.extra_data?.source_url || '';
	const explanationText = question?.explanation || questionPayload?.clinical_case?.explanation || '';
	const questionImage = resolveQuestionImagePayload(questionPayload);

	useEffect(() => {
		setHasImageError(false);
	}, [questionImage?.src]);

	const handleOpenSource = () => {
		if (!sourceUrl) return;
		if (onOpenSource) {
			onOpenSource(sourceUrl);
			return;
		}
		window.open(sourceUrl, '_blank', 'noopener,noreferrer');
	};

	return (
		<Box sx={{ width: '100%' }}>
			<Paper
				elevation={0}
				sx={{
					border: '1px solid #e2e8f0',
					borderRadius: '14px',
					p: { xs: 0.9, md: 1.8 },
					backgroundColor: '#ffffff',
				}}
			>
				<Stack
					direction="row"
					spacing={1}
					useFlexGap
					flexWrap="wrap"
					sx={{
						mb: 1.5,
						display: { xs: 'grid', sm: 'flex' },
						gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))' },
						columnGap: { xs: 1 },
						rowGap: { xs: 1 },
					}}
				>
					{FILTER_ORDER.map((key) => {
						const isActive = activeFilter === key;
						const isIncorrect = key === 'incorrect';
						const isCorrect = key === 'correct';
						const badgeTone = isIncorrect
							? { fg: '#dc2626', bg: '#fee2e2' }
							: isCorrect
							? { fg: '#15803d', bg: '#dcfce7' }
							: { fg: '#4338ca', bg: '#e0e7ff' };

						return (
							<Button
								key={key}
								variant="outlined"
								size="small"
								onClick={() => onFilterChange?.(key)}
								sx={{
									textTransform: 'none',
									width: { xs: '100%', sm: 'auto' },
									minWidth: { xs: 0, sm: 'unset' },
									justifyContent: 'flex-start',
									alignItems: 'center',
									borderRadius: '10px',
									borderColor: isActive
										? isIncorrect
											? '#fca5a5'
											: isCorrect
											? '#86efac'
											: '#a5b4fc'
										: '#e2e8f0',
									color: isActive
										? isIncorrect
											? '#dc2626'
											: isCorrect
											? '#15803d'
											: '#4338ca'
										: '#334155',
									backgroundColor: isActive
										? isIncorrect
											? '#fef2f2'
											: isCorrect
											? '#f0fdf4'
											: '#eef2ff'
										: '#ffffff',
									px: { xs: 1, sm: 1.2 },
									minHeight: { xs: 34, sm: 36 },
									'& .MuiButton-startIcon': {
										mr: 0.6,
										ml: 0,
										display: 'flex',
										alignItems: 'center',
									},
									'&:hover': {
										borderColor: '#a5b4fc',
										backgroundColor: isActive ? undefined : '#f8fafc',
									},
								}}
								startIcon={
									key === 'incorrect' ? (
										<XCircle size={14} />
									) : key === 'correct' ? (
										<CheckCircle2 size={14} />
									) : null
								}
							>
								<Stack direction="row" spacing={0.6} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
									<Typography sx={{ fontSize: { xs: 11, sm: 12 }, fontWeight: 600, lineHeight: 1, display: 'flex', alignItems: 'center' }}>{FILTER_LABELS[key]}</Typography>
									<Box
										sx={{
											fontSize: { xs: 10.5, sm: 11 },
											fontWeight: 700,
											px: 0.8,
											py: 0.2,
											borderRadius: '9999px',
											ml: 'auto',
											display: 'flex',
											alignItems: 'center',
											lineHeight: 1,
											color: badgeTone.fg,
											backgroundColor: badgeTone.bg,
										}}
									>
										{mergedFilters[key]}
									</Box>
								</Stack>
							</Button>
						);
					})}
				</Stack>

				<Paper
					elevation={0}
					sx={{
						border: '1px solid #e2e8f0',
						borderRadius: '12px',
						p: { xs: 1, md: 1.8 },
						backgroundColor: '#ffffff',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: { xs: 'flex-start', md: 'center' },
							justifyContent: 'space-between',
							flexDirection: { xs: 'column', md: 'row' },
							gap: 1,
						}}
					>
						<Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
							<Chip
								label={statusTone.label}
								icon={statusTone.icon}
								size="small"
								sx={{
									height: 30,
									borderRadius: '9px',
									border: `1px solid ${statusTone.border}`,
									backgroundColor: statusTone.bg,
									color: statusTone.fg,
									'& .MuiChip-icon': { color: statusTone.fg },
									'& .MuiChip-label': { px: 1.2, fontWeight: 600 },
								}}
							/>

							<Typography
								sx={{
									fontSize: 14,
									color: '#334155',
									fontWeight: 600,
									display: 'flex',
									alignItems: 'center',
									minHeight: 30,
								}}
							>
								Pregunta {questionPosition}
								{questionTotal > 0 ? ` de ${questionTotal}` : ''}
							</Typography>
						</Stack>

						<Stack
							direction="row"
							spacing={0.8}
							useFlexGap
							flexWrap="wrap"
							sx={{
								width: { xs: '100%', md: 'auto' },
								justifyContent: 'center',
								alignSelf: { xs: 'stretch', md: 'center' },
							}}
						>
							{topic?.name ? (
								<Chip
									label={topic.name}
									size="small"
									sx={{
										width: { xs: '100%', sm: 'auto' },
										maxWidth: '100%',
										minWidth: 0,
										borderRadius: '9px',
										border: '1px solid #bfdbfe',
										backgroundColor: '#eff6ff',
										color: '#2563eb',
										'& .MuiChip-label': {
											fontWeight: 600,
											display: 'block',
											maxWidth: '100%',
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
										},
									}}
								/>
							) : null}

							{specialty?.name ? (
								<Chip
									label={specialty.name}
									size="small"
									sx={{
										width: { xs: '100%', sm: 'auto' },
										maxWidth: '100%',
										minWidth: 0,
										borderRadius: '9px',
										border: '1px solid #cbd5e1',
										backgroundColor: '#f8fafc',
										color: '#334155',
										'& .MuiChip-label': {
											fontWeight: 600,
											display: 'block',
											maxWidth: '100%',
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
										},
									}}
								/>
							) : null}

							<Chip
								label={<span style={{ color: difficultyTone.fg }}>{difficultyTone.label}</span>}
								size="small"
								style={{
									border: `1px solid ${difficultyTone.border}`,
									backgroundColor: difficultyTone.bg,
									color: difficultyTone.fg,
								}}
								sx={{
									width: { xs: '100%', sm: 'auto' },
									maxWidth: '100%',
									minWidth: 0,
									borderRadius: '9px',
									border: `1px solid ${difficultyTone.border} !important`,
									backgroundColor: `${difficultyTone.bg} !important`,
									color: `${difficultyTone.fg} !important`,
									'& .MuiChip-label': {
										color: `${difficultyTone.fg} !important`,
										fontWeight: 700,
										display: 'block',
										maxWidth: '100%',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
									},
								}}
							/>
						</Stack>
					</Box>

					<Box sx={{ mt: 2 }}>
						{question?.is_clinical_case_question ? (
							<Typography sx={{ fontSize: { xs: 15, md: 17 }, color: '#0f172a', fontWeight: 700, mb: 0.7 }}>
								Caso clinico
							</Typography>
						) : null}

						<Typography sx={{ fontSize: { xs: 14, md: 15 }, lineHeight: 1.6, color: '#1e293b' }}>
							{capitalizeFirstLetter(
								questionPayload?.clinical_case?.description || questionPayload?.clinical_case?.history || ''
							)}
						</Typography>

						{questionImage && !hasImageError ? (
							<Box sx={{ mt: 1.6, display: 'flex', justifyContent: 'center' }}>
								<Box
									component="img"
									src={questionImage.src}
									alt={questionImage.alt}
									onError={() => {
										setHasImageError(true);
									}}
									sx={{
										maxWidth: '100%',
										width: { xs: '100%', sm: 'auto' },
										maxHeight: { xs: 260, md: 360 },
										objectFit: 'contain',
										borderRadius: '12px',
										border: '1px solid #e2e8f0',
										backgroundColor: '#ffffff',
										p: 0.5,
									}}
								/>
							</Box>
						) : null}

						<Typography sx={{ mt: 1.6, fontSize: { xs: 16, md: 20 }, color: '#0f172a', lineHeight: 1.3, fontWeight: 700 }}>
							{capitalizeFirstLetter(question?.question_text || 'Sin texto de pregunta')}
						</Typography>
					</Box>

					<Stack spacing={1.3} sx={{ mt: 2.2 }}>
						{options.map((option) => {
							const tone = normalizeOptionState(option, result);
							const numberLabel = optionNumber(option?.position);

							return (
								<Box
									key={option?.id || `${option?.position}-${option?.option_text}`}
									sx={{
										border: `1px solid ${tone.border}`,
										backgroundColor: tone.bg,
										borderRadius: '12px',
										px: { xs: 1.1, md: 1.6 },
										py: { xs: 1.1, md: 1.2 },
										display: 'flex',
										alignItems: { xs: 'flex-start', md: 'center' },
										justifyContent: 'space-between',
										flexDirection: { xs: 'column', md: 'row' },
										gap: 1,
									}}
								>
									<Stack direction="row" spacing={1.1} alignItems="center" sx={{ minWidth: 0 }}>
										<Box
											sx={{
												width: 30,
												height: 30,
												borderRadius: '9999px',
												border: `1px solid ${tone.ring}`,
												color: tone.accent,
												fontWeight: 700,
												fontSize: 16,
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												flexShrink: 0,
											}}
										>
											{numberLabel}
										</Box>

										<Typography sx={{ fontSize: { xs: 14, md: 18 }, color: '#0f172a', lineHeight: 1.4 }}>
											{capitalizeFirstLetter(option?.option_text)}
										</Typography>
									</Stack>

									{tone.badge ? (
										<Box
											sx={{
												px: 1,
												py: 0.55,
												borderRadius: '9px',
												backgroundColor: tone.badgeBg,
												color: tone.badgeFg,
												fontSize: 11,
												fontWeight: 700,
												whiteSpace: 'nowrap',
												flexShrink: 0,
												alignSelf: { xs: 'flex-start', md: 'center' },
											}}
										>
											{tone.badge}
										</Box>
									) : null}
								</Box>
							);
						})}
					</Stack>

					<Paper
						elevation={0}
						sx={{
							mt: 1.7,
							border: '1px solid #86efac',
							borderRadius: '12px',
							px: { xs: 1.1, md: 1.6 },
							py: { xs: 1.1, md: 1.25 },
							backgroundColor: '#f0fdf4',
						}}
					>
						<Stack direction="row" spacing={1} alignItems="center" sx={{ color: '#15803d' }}>
							<Lightbulb size={18} />
							<Typography sx={{ fontSize: { xs: 16, md: 19 }, fontWeight: 700 }}>Explicacion</Typography>
						</Stack>

						<Typography sx={{ mt: 1, fontSize: { xs: 14, md: 17 }, color: '#1f2937', lineHeight: 1.55 }}>
							{explanationText || 'Sin explicacion disponible para esta pregunta.'}
						</Typography>

						<Box
							sx={{
								mt: 1.1,
								display: 'flex',
								alignItems: { xs: 'flex-start', md: 'center' },
								justifyContent: 'space-between',
								flexDirection: { xs: 'column', md: 'row' },
								gap: 1,
							}}
						>
							  <Typography sx={{ fontSize: 12, color: '#64748b' }}>Fuente: {sourceLabel}</Typography>

							<Button
								size="small"
								variant="outlined"
								onClick={handleOpenSource}
								disabled={!sourceUrl}
								endIcon={<ExternalLink size={14} />}
								sx={{
									width: { xs: '100%', sm: 'auto' },
									textTransform: 'none',
									borderColor: '#cbd5e1',
									color: '#334155',
									borderRadius: '9px',
									'&:hover': {
										borderColor: '#94a3b8',
										backgroundColor: '#f8fafc',
									},
								}}
							>
								Ver guia completa
							</Button>
						</Box>
					</Paper>
				</Paper>

				<Box
					sx={{
						mt: 1.7,
						display: 'grid',
						gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
						gap: 1,
						alignItems: 'center',
					}}
				>
					<Button
						variant="outlined"
						startIcon={<ArrowLeft size={17} />}
						onClick={onPrevious}
						disabled={disablePrevious}
						sx={{
							width: { xs: '100%', md: 'auto' },
							justifySelf: { md: 'start' },
							textTransform: 'none',
							borderRadius: '10px',
							borderColor: '#a5b4fc',
							color: '#4f46e5',
							minHeight: 44,
							fontWeight: 600,
							'&:hover': {
								borderColor: '#6366f1',
								backgroundColor: '#eef2ff',
							},
						}}
					>
						Anterior
					</Button>

					<Button
						variant="outlined"
						startIcon={<FileText size={16} />}
						onClick={onViewDetail}
						sx={{
							width: { xs: '100%', md: 'auto' },
							textTransform: 'none',
							borderRadius: '10px',
							borderColor: '#cbd5e1',
							color: '#334155',
							minHeight: 44,
							px: 2.2,
							fontWeight: 600,
							'&:hover': {
								borderColor: '#94a3b8',
								backgroundColor: '#f8fafc',
							},
						}}
					>
						Ver detalle
					</Button>

					<Button
						variant="contained"
						endIcon={<ArrowRight size={17} />}
						onClick={onNext}
						disabled={disableNext}
						sx={{
							width: { xs: '100%', md: 'auto' },
							justifySelf: { md: 'end' },
							textTransform: 'none',
							borderRadius: '10px',
							backgroundColor: '#6366f1',
							minHeight: 44,
							px: 2.5,
							fontWeight: 700,
							'&:hover': {
								backgroundColor: '#4f46e5',
							},
						}}
					>
						Siguiente
					</Button>
				</Box>

				{result?.status === 'unanswered' ? (
					<Stack direction="row" spacing={0.8} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mt: 1.2, color: '#64748b' }}>
						<AlertCircle size={14} />
						<Typography sx={{ fontSize: 11 }}>Esta pregunta no fue respondida en el examen.</Typography>
					</Stack>
				) : null}
			</Paper>
		</Box>
	);
}

